import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { COMPANY } from "@/lib/translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  name: "",
  phone: "",
  email: "",
  pickup: "",
  destination: "",
  date: "",
  time: "",
  passengers: 1,
  notes: "",
};

const BookingForm = () => {
  const { t, lang } = useLang();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const destinationRef = useRef(null);

  // Pre-fill destination when a service card is clicked
  useEffect(() => {
    const handlePrefill = (e) => {
      const dest = e?.detail?.destination;
      if (!dest) return;
      setForm((s) => ({ ...s, destination: dest }));
      // Briefly highlight + focus the destination input
      const el = destinationRef.current;
      if (el) {
        setTimeout(() => {
          el.focus({ preventScroll: true });
          el.classList.add("ring-prefill");
          setTimeout(() => el.classList.remove("ring-prefill"), 1600);
        }, 450);
      }
      toast.success(`${dest} ✓`, { duration: 1800 });
    };
    window.addEventListener("faustitur:prefill-booking", handlePrefill);
    return () => window.removeEventListener("faustitur:prefill-booking", handlePrefill);
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: name === "passengers" ? Number(value) : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.pickup || !form.destination || !form.date || !form.time) {
      toast.error(t.booking.requiredErr);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        email: form.email && form.email.trim() ? form.email.trim() : null,
        notes: form.notes && form.notes.trim() ? form.notes.trim() : null,
        language: lang,
      };
      await axios.post(`${API}/bookings`, payload);
      setDone(true);
      toast.success(t.booking.success);
      setForm(initialState);
    } catch (err) {
      console.error(err);
      toast.error(t.booking.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="relative py-24 sm:py-32 bg-[color:var(--bg-primary)]"
    >
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute -top-32 -right-24 w-[480px] h-[480px] rounded-full blur-3xl bg-[color:var(--gold)]/10" />
      </div>

      <div className="relative max-w-5xl mx-auto px-5 sm:px-8">
        <div className="text-center">
          <p className="label-eyebrow">{t.booking.eyebrow}</p>
          <h2
            data-testid="booking-title"
            className="font-display text-3xl sm:text-4xl lg:text-5xl mt-4 text-white tracking-tight"
          >
            {t.booking.title}
          </h2>
          <p className="mt-5 text-zinc-400 max-w-xl mx-auto">{t.booking.subtitle}</p>
        </div>

        <form
          onSubmit={handleSubmit}
          data-testid="booking-form"
          className="mt-12 bg-[color:var(--bg-surface)] border border-white/10 p-6 sm:p-10 grid grid-cols-1 sm:grid-cols-2 gap-5"
        >
          <Field label={t.booking.fields.name} required>
            <input
              data-testid="booking-name"
              name="name"
              value={form.name}
              onChange={onChange}
              className="input-dark"
              placeholder="João Silva"
              autoComplete="name"
            />
          </Field>
          <Field label={t.booking.fields.phone} required>
            <input
              data-testid="booking-phone"
              name="phone"
              value={form.phone}
              onChange={onChange}
              className="input-dark"
              placeholder="+351 9XX XXX XXX"
              autoComplete="tel"
            />
          </Field>
          <Field label={t.booking.fields.email} className="sm:col-span-2">
            <input
              data-testid="booking-email"
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
              className="input-dark"
              placeholder="email@exemplo.com"
              autoComplete="email"
            />
          </Field>
          <Field label={t.booking.fields.pickup} required>
            <input
              data-testid="booking-pickup"
              name="pickup"
              value={form.pickup}
              onChange={onChange}
              className="input-dark"
              placeholder="Hotel / Morada"
            />
          </Field>
          <Field label={t.booking.fields.destination} required>
            <input
              ref={destinationRef}
              data-testid="booking-destination"
              name="destination"
              value={form.destination}
              onChange={onChange}
              className="input-dark"
              placeholder="Aeroporto de Faro, Zoomarine..."
            />
          </Field>
          <Field label={t.booking.fields.date} required>
            <input
              data-testid="booking-date"
              name="date"
              type="date"
              value={form.date}
              onChange={onChange}
              className="input-dark"
            />
          </Field>
          <Field label={t.booking.fields.time} required>
            <input
              data-testid="booking-time"
              name="time"
              type="time"
              value={form.time}
              onChange={onChange}
              className="input-dark"
            />
          </Field>
          <Field label={t.booking.fields.passengers}>
            <input
              data-testid="booking-passengers"
              name="passengers"
              type="number"
              min={1}
              max={6}
              value={form.passengers}
              onChange={onChange}
              className="input-dark"
            />
          </Field>
          <Field label={t.booking.fields.notes} className="sm:col-span-2">
            <textarea
              data-testid="booking-notes"
              name="notes"
              value={form.notes}
              onChange={onChange}
              rows={3}
              className="input-dark resize-none"
              placeholder="..."
            />
          </Field>

          <div className="sm:col-span-2 flex flex-col sm:flex-row items-center justify-between gap-4 pt-3">
            <p className="text-xs text-zinc-500">
              <span className="gold-text">●</span> {COMPANY.phoneDisplay} ·{" "}
              {COMPANY.base}
            </p>
            <button
              type="submit"
              disabled={loading}
              data-testid="booking-submit"
              className="btn-gold w-full sm:w-auto justify-center disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> {t.booking.sending}
                </>
              ) : done ? (
                <>
                  <CheckCircle2 className="h-4 w-4" /> {t.booking.success}
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" /> {t.booking.submit}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

const Field = ({ label, children, required, className = "" }) => (
  <label className={`flex flex-col gap-2 ${className}`}>
    <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">
      {label}
      {required && <span className="text-[color:var(--gold)]"> *</span>}
    </span>
    {children}
  </label>
);

export default BookingForm;
